import { Map } from 'immutable';

import { shapeFaireDistanceUnitForApi, shapeFaireMassUnitForApi, shapeFairePricesForApi } from './shapeAttributes';
import { getDateKey } from '../time';

import { AMOUNT, ATTRIBUTE_DEFAULTS, VALUE } from '../../constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';

export function getInventoryOperationValue({
  channel,
  combination = null,
  offeringId = null,
  inventory = EMPTY.MAP,
  toImmutable = true,
  type,
  value: source,
}) {
  function wrapOperationValue(operationValue) {
    return toImmutable
      ? Map(operationValue)
      : operationValue;
  }

  switch (channel) {
    case EBAY: {
      const value = source === undefined
        ? inventory.getIn([type, VALUE])
        : source;

      return type === ATTRIBUTE.PRICE
        ? wrapOperationValue({ value })
        : value;
    }

    case ETSY: {
      const value = source === undefined
        ? inventory.getIn([type, VALUE])
        : source;

      return wrapOperationValue({
        combination,
        offeringId,
        value: type === ATTRIBUTE.PRICE
          ? wrapOperationValue({ rounding: '', value })
          : value,
      });
    }

    case FAIRE: {
      switch (type) {
        case ATTRIBUTE.DISTANCE_UNIT: {
          const value = source === undefined
            ? inventory.getIn([type, VALUE])
            : source;

          return shapeFaireDistanceUnitForApi(value);
        }

        case ATTRIBUTE.DEADLINE_DATE:
        case ATTRIBUTE.END_DATE:
        case ATTRIBUTE.START_DATE: {
          const value = source === undefined
            ? inventory.getIn([type, VALUE])
            : source;

          return value instanceof Date
            ? getDateKey(value)
            : null;
        }

        case ATTRIBUTE.MASS_UNIT: {
          const value = source === undefined
            ? inventory.getIn([type, VALUE])
            : source;

          return shapeFaireMassUnitForApi(value);
        }

        case ATTRIBUTE.RESTOCK_DATE: {
          const value = source === undefined
            ? inventory.getIn([type, VALUE])
            : source;

          return value instanceof Date
            ? value.toISOString()
            : null;
        }

        case ATTRIBUTE.RETAIL_PRICE:
        case ATTRIBUTE.WHOLESALE_PRICE: {
          return shapeFairePricesForApi({ inventory, toImmutable });
        }

        default: {
          const value = source === undefined
            ? inventory.getIn([type, VALUE])
            : source;

          return value;
        }
      }
    }

    case SHOPIFY: {
      const barcode = inventory.getIn([ATTRIBUTE.BARCODE, VALUE]) || null;
      const cap = inventory.getIn([ATTRIBUTE.CAP, VALUE]) || AMOUNT.ZERO_CENT;
      const countryCode = inventory.getIn([ATTRIBUTE.COUNTRY_CODE, VALUE]) || null;
      const cpi = inventory.getIn([ATTRIBUTE.CPI, VALUE]) || AMOUNT.ZERO_CENT;
      const hsCode = inventory.getIn([ATTRIBUTE.HS_CODE, VALUE]) || null;
      const inventoryPolicy = inventory.getIn([ATTRIBUTE.CONTINUE_SELLING, VALUE]) ? 'continue' : 'deny';
      const inventoryManagement = inventory.getIn([ATTRIBUTE.TRACK_QUANTITY, VALUE]) ? SHOPIFY : null;
      const price = inventory.getIn([ATTRIBUTE.PRICE, VALUE]) || AMOUNT.ZERO_CENT;
      const quantity = inventory.getIn([ATTRIBUTE.QUANTITY, VALUE]) || AMOUNT.ZERO;
      const sku = inventory.getIn([ATTRIBUTE.SKU, VALUE]) || null;
      const weight = inventory.getIn([ATTRIBUTE.WEIGHT, VALUE]) || AMOUNT.ZERO;
      const unit = inventory.getIn([ATTRIBUTE.UNIT, VALUE]) || ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.UNIT];
      const chargeTax = inventory.getIn([ATTRIBUTE.CHARGE_TAX, VALUE]);
      const taxable = typeof chargeTax === 'boolean' ? chargeTax : true;
      const physical = inventory.getIn([ATTRIBUTE.PHYSICAL, VALUE]);
      const requiresShipping = typeof physical === 'boolean' ? physical : true;
      const operationValue = inventory.get(ATTRIBUTE.DEFAULT_VALUE)
        .setIn(['variants', 0, 'shopify_barcode'], barcode)
        .setIn(['variants', 0, 'shopify_compare_at_price'], cap)
        .setIn(['variants', 0, 'shopify_cost'], cpi)
        .setIn(['variants', 0, 'shopify_country_code_of_origin'], countryCode)
        .setIn(['variants', 0, 'shopify_harmonized_system_code'], hsCode)
        .setIn(['variants', 0, 'shopify_inventory_management'], inventoryManagement)
        .setIn(['variants', 0, 'shopify_inventory_policy'], inventoryPolicy)
        .setIn(['variants', 0, 'shopify_inventory_quantity'], quantity)
        .setIn(['variants', 0, 'shopify_price'], price)
        .setIn(['variants', 0, 'shopify_requires_shipping'], requiresShipping)
        .setIn(['variants', 0, 'shopify_sku'], sku)
        .setIn(['variants', 0, 'shopify_taxable'], taxable)
        .setIn(['variants', 0, 'shopify_weight'], weight)
        .setIn(['variants', 0, 'shopify_weight_unit'], unit);

      return toImmutable
        ? operationValue
        : operationValue.toJS();
    }

    default: {
      return undefined;
    }
  }
}
